import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

interface ScrollRevealProps {
  children: ReactNode;
  className?: string;
  /** Delay in ms for stagger effects */
  delay?: number;
  /** Animation direction */
  direction?: "up" | "left" | "right";
  /** Whether to disable the animation */
  disabled?: boolean;
}

/**
 * ScrollReveal — wrapper component that fades+slides children
 * into view when they enter the viewport.
 * 
 * Uses IntersectionObserver, respects prefers-reduced-motion,
 * and fires only once per element.
 */
const ScrollReveal = ({
  children,
  className,
  delay = 0,
  direction = "up",
  disabled = false,
}: ScrollRevealProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (disabled) {
      setIsVisible(true);
      return;
    }

    // Respect reduced motion
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setIsVisible(true);
      return;
    }

    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [disabled]);

  const translateStart = {
    up: "translate-y-6",
    left: "-translate-x-6",
    right: "translate-x-6",
  }[direction];

  return (
    <div
      ref={ref}
      className={cn(
        "transition-all duration-[600ms] ease-out",
        isVisible ? "opacity-100 translate-y-0 translate-x-0" : `opacity-0 ${translateStart}`,
        className
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

export default ScrollReveal;
